import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  MessageSquare,
  AtSign,
  Users,
  BookOpen,
  MessageCircle,
  Star,
  Briefcase,
  Info,
  CheckCheck,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useNotificationsStore } from '../stores/notifications-store';
import type { AppNotification, NotificationType } from '../lib/api';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from './ui/responsive-modal';

// ─── Icon map per notification type ───────────────────────────────────────────

const TYPE_ICONS: Record<NotificationType, typeof Bell> = {
  message: MessageSquare,
  mention: AtSign,
  collaboration_invite: Users,
  project_shared: BookOpen,
  comment: MessageCircle,
  review: Star,
  hire_request: Briefcase,
  system: Info,
};

// ─── Accent map — a=âmbar (conquistas/oportunidades), b=aurora (social/mensagens),
//     c=azul (sistema), espelhando o contrato visual de #scr-notificacoes ──────

const TYPE_ACCENT: Record<NotificationType, 'a' | 'b' | 'c'> = {
  review: 'a',
  hire_request: 'a',
  message: 'b',
  mention: 'b',
  comment: 'b',
  collaboration_invite: 'b',
  project_shared: 'b',
  system: 'c',
};

// ─── Relative time helper ─────────────────────────────────────────────────────

function relativeTime(dateStr: string, t: (key: string, defaultValue: string) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return t('notificationsPanel.now', 'agora');
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

// ─── Navigation helper ────────────────────────────────────────────────────────

function getNotificationUrl(notification: AppNotification): string | null {
  const { data } = notification;
  if (data.conversationId) return `/app/messages?c=${data.conversationId}`;
  if (data.projectId) return `/app/editor/${data.projectId}`;
  if (data.url) return data.url as string;
  return null;
}

// ─── Single notification row ─────────────────────────────────────────────────

interface NotificationItemProps {
  notification: AppNotification;
  onClose?: () => void;
}

function NotificationItem({ notification, onClose }: NotificationItemProps) {
  const { t } = useTranslation();
  const { markRead } = useNotificationsStore();
  const navigate = useNavigate();
  const Icon = TYPE_ICONS[notification.type] ?? Bell;
  const accent = TYPE_ACCENT[notification.type] ?? 'c';

  const handleClick = useCallback(async () => {
    if (!notification.read) {
      await markRead(notification.id);
    }
    const url = getNotificationUrl(notification);
    if (url) {
      navigate(url);
      onClose?.();
    }
  }, [notification, markRead, navigate, onClose]);

  return (
    <button
      onClick={handleClick}
      className={cn('ntf w-full text-left', !notification.read && 'unread')}
    >
      <i className={accent}>
        <Icon size={15} aria-hidden />
      </i>
      <span className="flex-1 min-w-0">
        <b>{notification.title}</b>
        {notification.body && (
          <small className="line-clamp-2">{notification.body}</small>
        )}
      </span>
      <span className="tm">{relativeTime(notification.createdAt, t)}</span>
    </button>
  );
}

// ─── Notifications panel content (shared between bell popover & page) ─────────

interface NotificationsPanelContentProps {
  onClose?: () => void;
  /** When true, shows only the first N items (for the bell panel) */
  limit?: number;
}

export function NotificationsPanelContent({
  onClose,
  limit,
}: NotificationsPanelContentProps) {
  const { t } = useTranslation();
  const { notifications, unreadCount, markAllRead, isLoading } = useNotificationsStore();
  const navigate = useNavigate();

  const items = limit ? notifications.slice(0, limit) : notifications;

  return (
    <div className="flex flex-col min-h-0">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <span className="text-sm font-semibold text-foreground">{t('notificationsPanel.title', 'Notificações')}</span>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllRead()}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <CheckCheck size={13} />
            {t('notificationsPanel.markAllRead', 'Marcar todas como lidas')}
          </button>
        )}
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1 p-2">
        {isLoading && items.length === 0 && (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            {t('notificationsPanel.loading', 'Carregando...')}
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <Bell size={28} className="text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">{t('notificationsPanel.empty', 'Nenhuma notificação')}</p>
          </div>
        )}

        {items.map((n) => (
          <NotificationItem key={n.id} notification={n} onClose={onClose} />
        ))}
      </div>

      {/* Footer: link to full page */}
      {limit && notifications.length > 0 && (
        <div className="border-t border-border px-4 py-2.5">
          <button
            onClick={() => { navigate('/app/notifications'); onClose?.(); }}
            className="w-full text-center text-xs text-primary hover:underline"
          >
            {t('notificationsPanel.viewAll', 'Ver todas as notificações')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Bell button with badge ───────────────────────────────────────────────────

interface NotificationsBellProps {
  className?: string;
}

export function NotificationsBell({ className }: NotificationsBellProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const fetchNotifications = useNotificationsStore((s) => s.fetchNotifications);

  const handleOpen = () => {
    setOpen(true);
    fetchNotifications();
  };

  return (
    <>
      <button
        onClick={handleOpen}
        aria-label={
          unreadCount > 0
            ? t('notificationsPanel.bellLabelUnread', 'Notificações — {{count}} não lidas', { count: unreadCount })
            : t('notificationsPanel.title', 'Notificações')
        }
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
          className
        )}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground shadow-sm">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <ResponsiveModal open={open} onOpenChange={setOpen}>
        <ResponsiveModalContent
          className="p-0 overflow-hidden sm:max-w-sm max-h-[80vh]"
          showCloseButton={false}
        >
          <ResponsiveModalHeader className="sr-only">
            <ResponsiveModalTitle>{t('notificationsPanel.title', 'Notificações')}</ResponsiveModalTitle>
          </ResponsiveModalHeader>
          <NotificationsPanelContent onClose={() => setOpen(false)} limit={20} />
        </ResponsiveModalContent>
      </ResponsiveModal>
    </>
  );
}
